import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: { delay, duration: 0.8, ease: "easeOut" }
    })
  };

  const stats = [
    { value: "11+", label: "Années d'Expérience" },
    { value: "500+", label: "Clients Satisfaits" },
    { value: "12", label: "Secteurs d'Activité" },
    { value: "98%", label: "Fidélisation Client" }
  ];

  return (
    <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">

      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] brightness-100 contrast-150 mix-blend-overlay"></div>

      {/* Blobs */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute top-0 left-0 w-[800px] h-[800px] bg-blue-500 opacity-15 rounded-full blur-[150px]"
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 8, repeat: Infinity, delay: 2 }}
        className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-amber-500 opacity-10 rounded-full blur-[120px]"
      />

      {/* Rotating decoration */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="absolute -top-24 -left-24 w-64 h-64 border border-dashed border-white/10 rounded-full"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
        className="absolute -bottom-16 -right-16 w-48 h-48 border-2 border-white/5 rounded-full"
      />

      {/* CONTENU */}
      <div className="relative z-10 w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-20 xl:px-32 py-20 lg:py-28">
        <div className="text-center space-y-8">

          {/* Badge */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={0}
            variants={textVariants}
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-md rounded-full border border-white/15 shadow-lg"
          >
            <Icon name="Award" size={18} className="text-amber-400" />
            <span className="text-xs md:text-sm font-medium tracking-wide uppercase text-amber-200">
              Confiance Depuis 2015
            </span>
          </motion.div>

          {/* Titre */}
          <motion.h1
            initial="hidden"
            animate="visible"
            custom={0.2}
            variants={textVariants}
            className="text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight tracking-tight text-white"
          >
            Bâtir la Confiance par
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
              l'Excellence en RDC
            </span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial="hidden"
            animate="visible"
            custom={0.4}
            variants={textVariants}
            className="text-lg md:text-xl lg:text-2xl text-blue-100/80 leading-relaxed max-w-4xl mx-auto"
          >
            ORAZEK Investment se positionne comme le principal fournisseur de solutions complètes en République Démocratique du Congo, offrant un professionnalisme de niveau corporatif à travers 12 secteurs d'activité.
          </motion.p>

          {/* Stats */}
          <motion.div
            initial="hidden"
            animate="visible"
            custom={0.6}
            variants={textVariants}
            className="flex flex-wrap items-center justify-center gap-8 md:gap-12 lg:gap-16 pt-8"
          >
            {stats.map((stat, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <div className="hidden md:block w-px h-16 bg-white/10"></div>
                )}
                <div className="text-center">
                  <div className="text-3xl md:text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-300 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-blue-200/60 uppercase tracking-wider font-semibold">
                    {stat.label}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Down */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 cursor-pointer z-20"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        onClick={() => window.scrollTo({ top: window.innerHeight * 0.7, behavior: 'smooth' })}
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-blue-300/60 font-semibold">Découvrir</span>
          <div className="w-5 h-8 border-2 border-white/20 rounded-full flex justify-center pt-2">
            <motion.div
              className="w-1 h-1 bg-white/50 rounded-full"
              animate={{ y: [0, 8, 0], opacity: [1, 0, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
